import React, { useState } from 'react';
import { 
  ShoppingBag, 
  Search, 
  Filter, 
  Tag, 
  Truck, 
  Calendar, 
  Users, 
  ArrowRight, 
  Star,
  Coffee,
  Leaf,
  Sprout,
  Zap,
  CheckCircle,
  Clock,
  DollarSign,
  MapPin
} from 'lucide-react';
import { motion } from 'framer-motion';

// Sample marketplace listings
const listings = [
  {
    id: 1,
    title: "Premium Arabica Coffee Beans",
    seller: "Edward's Hamlet Cooperative",
    location: "Jinja, Uganda",
    price: "$4.50",
    unit: "per kg",
    quantity: "500 kg available",
    category: "coffee",
    image: "https://images.unsplash.com/photo-1611854779393-1b2da9d400fe",
    rating: 4.8,
    reviews: 24,
    verified: true,
    organic: true,
    delivery: "Available within 50km",
    description: "Single-origin Arabica beans grown at 1,500m altitude using regenerative farming practices. Medium roast with notes of chocolate and citrus."
  },
  {
    id: 2,
    title: "Organic Robusta Coffee",
    seller: "Mbale Farmers Association",
    location: "Mbale, Uganda",
    price: "$3.80",
    unit: "per kg",
    quantity: "350 kg available",
    category: "coffee",
    image: "https://images.unsplash.com/photo-1559525839-b184a4d698c7",
    rating: 4.6,
    reviews: 18,
    verified: true,
    organic: true,
    delivery: "Available within 30km",
    description: "Bold, full-bodied Robusta coffee beans grown using traditional methods. Perfect for espresso blends with rich crema."
  },
  {
    id: 3,
    title: "Coffee Cherry Syrup",
    seller: "Edward's Hamlet Processing",
    location: "Jinja, Uganda",
    price: "$8.20",
    unit: "per bottle",
    quantity: "120 bottles available",
    category: "value-added",
    image: "https://images.unsplash.com/photo-1581996323777-9fde24488c9b",
    rating: 4.9,
    reviews: 12,
    verified: true,
    organic: true,
    delivery: "Nationwide shipping",
    description: "Natural sweetener made from coffee cherry pulp. Rich in antioxidants with a unique fruity flavor. Perfect for beverages and desserts."
  },
  {
    id: 4,
    title: "Dried Cocoa Beans",
    seller: "Kampala Cocoa Collective",
    location: "Kampala, Uganda",
    price: "$5.20",
    unit: "per kg",
    quantity: "200 kg available",
    category: "cocoa",
    image: "https://images.unsplash.com/photo-1589881133595-a3c085cb731d",
    rating: 4.7,
    reviews: 15,
    verified: true,
    organic: false,
    delivery: "Pickup only",
    description: "Premium fermented and sun-dried cocoa beans with rich chocolate flavor profile. Ideal for craft chocolate makers."
  }
];

const CommunityMarketplace: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedListing, setSelectedListing] = useState<any>(null);

  const filteredListings = listings.filter(listing => {
    const matchesSearch = listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         listing.seller.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || listing.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 4 }}
            className="bg-avn-purple-100 p-2 rounded-lg"
          >
            <ShoppingBag className="h-5 w-5 text-avn-purple-600" />
          </motion.div>
          <div>
            <h2 className="text-xl font-semibold">Community Marketplace</h2>
            <p className="text-sm text-gray-500">Connect directly with verified producers</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="h-4 w-4" />
          <span className="text-sm">Verified Sellers</span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search products or sellers..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="relative min-w-[150px]">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <select
            className="w-full pl-10 pr-4 py-2 border rounded-lg appearance-none bg-white focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="coffee">Coffee</option>
            <option value="cocoa">Cocoa</option>
            <option value="value-added">Value-Added Products</option>
          </select>
        </div>
      </div>

      {/* Listings */}
      {selectedListing ? (
        <div className="mb-6">
          <button 
            onClick={() => setSelectedListing(null)}
            className="text-avn-purple-600 mb-4 flex items-center gap-1 hover:underline"
          >
            ← Back to listings
          </button>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="h-64 rounded-lg overflow-hidden">
              <img 
                src={selectedListing.image} 
                alt={selectedListing.title}
                className="w-full h-full object-cover"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold">{selectedListing.title}</h3>
                <div className="flex items-center gap-1 text-amber-500">
                  <Star className="h-4 w-4 fill-current" />
                  <span>{selectedListing.rating}</span>
                  <span className="text-gray-500 text-sm">({selectedListing.reviews})</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-gray-600 mb-4">
                <Users className="h-4 w-4" />
                <span>{selectedListing.seller}</span>
                {selectedListing.verified && (
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">Verified</span>
                )}
              </div>
              
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-1 text-avn-purple-600 font-bold text-xl">
                  <DollarSign className="h-5 w-5" />
                  <span>{selectedListing.price}</span>
                  <span className="text-sm font-normal text-gray-500">{selectedListing.unit}</span>
                </div>
                <div className="text-sm text-gray-500">
                  {selectedListing.quantity}
                </div>
              </div>
              
              <p className="text-gray-700 mb-4">
                {selectedListing.description}
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="h-4 w-4 text-avn-purple-600" />
                  <span>{selectedListing.location}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Truck className="h-4 w-4 text-avn-purple-600" />
                  <span>{selectedListing.delivery}</span>
                </div>
                {selectedListing.organic && (
                  <div className="flex items-center gap-2 text-green-600">
                    <Leaf className="h-4 w-4" />
                    <span>Organic Certified</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4 text-avn-purple-600" />
                  <span>Listed 2 days ago</span>
                </div>
              </div>
              
              <div className="flex gap-4">
                <button className="flex-1 bg-avn-purple-600 text-white py-2 px-4 rounded-lg hover:bg-avn-purple-700 transition-colors flex items-center justify-center gap-2">
                  <ShoppingBag className="h-5 w-5" />
                  Contact Seller
                </button>
                <button className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
                  <Star className="h-5 w-5" />
                  Save Listing
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {filteredListings.map((listing) => (
            <motion.div
              key={listing.id}
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="border rounded-lg overflow-hidden hover:shadow-md cursor-pointer"
              onClick={() => setSelectedListing(listing)}
            >
              <div className="h-40 relative">
                <img 
                  src={listing.image} 
                  alt={listing.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  {listing.category === 'coffee' && (
                    <div className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">
                      <Coffee className="h-3 w-3 inline-block mr-1" />
                      Coffee
                    </div>
                  )}
                  {listing.category === 'cocoa' && (
                    <div className="bg-brown-100 text-brown-800 text-xs px-2 py-1 rounded-full">
                      <Sprout className="h-3 w-3 inline-block mr-1" />
                      Cocoa
                    </div>
                  )}
                  {listing.category === 'value-added' && (
                    <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      <Zap className="h-3 w-3 inline-block mr-1" />
                      Value-Added
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">{listing.title}</h3>
                  <div className="flex items-center gap-1 text-amber-500">
                    <Star className="h-4 w-4 fill-current" />
                    <span>{listing.rating}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-600">{listing.seller}</div>
                  <div className="text-avn-purple-600 font-bold">
                    {listing.price}
                    <span className="text-xs font-normal text-gray-500 ml-1">{listing.unit}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    <span>{listing.location}</span>
                  </div>
                  <div>{listing.quantity}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {filteredListings.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No listings found matching your criteria
        </div>
      )}

      {!selectedListing && (
        <div className="flex justify-center">
          <button className="text-avn-purple-600 hover:text-avn-purple-700 flex items-center gap-1">
            <span>View All Listings</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default CommunityMarketplace;